<script setup lang="ts">
import { computed, inject } from 'vue'
import UiText from './UiText.vue'
import UiIcon from './UiIcon.vue'
import { CheckboxGroupKey } from './shared/checkboxGroupContext'

export interface UiCheckboxProps {
  label?: string
  description?: string
  value?: string | number
  name?: string
  disabled?: boolean
}

const props = defineProps<UiCheckboxProps>()
const model = defineModel<boolean | Array<string | number> | undefined>()
const group = inject(CheckboxGroupKey, null)

const emit = defineEmits<{
  (e: 'validate'): void
}>()

const isChecked = computed(() => {
  if (Array.isArray(model.value)) {
    const v = props.value
    if (v === undefined) return false
    return model.value.includes(v)
  }
  if (group && props.value !== undefined) {
    return group.has(props.value)
  }
  return Boolean(model.value)
})

function toggle() {
  if (props.disabled) return
  if (group && props.value !== undefined) {
    group.toggle(props.value)
    emit('validate')
    return
  }
  if (Array.isArray(model.value)) {
    const v = props.value
    if (v === undefined) return
    const set = new Set(model.value)
    if (set.has(v)) set.delete(v)
    else set.add(v)
    model.value = Array.from(set)
  } else {
    model.value = !model.value
  }
  emit('validate')
}

const inputName = computed(() => props.name || group?.name)
</script>

<template>
  <button
    type="button"
    :name="inputName"
    class="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 select-none cursor-pointer text-left"
    :class="props.description ? 'items-start' : 'items-center'"
    :aria-pressed="isChecked"
    :disabled="props.disabled"
    @click="toggle"
  >
    <!-- Row 1, Col 1: Checkbox -->
    <span class="inline-flex items-center justify-center w-5 h-5 rounded border border-se-gray-300 row-start-1 col-start-1" :class="{
      'bg-se-gray-300': props.disabled,
      'bg-se-dark border-se-dark': isChecked && !props.disabled,
    }">
      <input type="checkbox" class="hidden" v-model="model" :value="props.value" :name="inputName" :disabled="props.disabled" />
      <UiIcon v-if="isChecked" name="check" class="w-3 h-3" :class="{
        'text-se-white': isChecked && !props.disabled,
        'text-se-gray-500': isChecked && props.disabled,
      }" />
    </span>
    <!-- Row 1, Col 2: Label -->
    <UiText v-if="props.label" as="label" variant="p-small" :class="['flex min-h-5 items-center text-left row-start-1 col-start-2 leading-tight', props.description ? 'font-semibold' : '']" :text="props.label" />
    <!-- Row 2, Col 2: Description -->
    <UiText v-if="props.description" as="span" variant="p-small" class="text-se-gray-500 text-left row-start-2 col-start-2 leading-snug" :text="props.description" />
    <slot />
  </button>
</template>
